import { useEffect, useState } from 'react';
import { filter, map } from 'rxjs/operators';

import type { HostCapabilitiesData } from '@sb/feathers-types';
import {
  HOST_CAPABILITIES_ID,
  HostCapabilitiesData as HostCapabilitiesSchema,
} from '@sb/feathers-types';
import { makeNamespacedLog } from '@sb/log';
import { isNotNull } from '@sb/utilities';

import { getService } from '../utils';

const log = makeNamespacedLog('hostCapabilities');

/**
 * The service answers with one object, but Feathers types every read as
 * possibly a list or a page. Narrow and validate in one place.
 *
 * Null rather than a throw on a payload we do not recognise: this runs per
 * event, and an rx stream that errors is finished, which would freeze every
 * gated surface until the page reloads. Skipping one update is the lesser
 * failure — and a browser older than the host it is talking to, which is the
 * way this happens, will disagree about the preset enum.
 */
function one(result: unknown): HostCapabilitiesData | null {
  const candidate = Array.isArray(result) ? result[0] : result;

  const parsed = HostCapabilitiesSchema.safeParse(candidate);

  if (!parsed.success) {
    log.error('onLoadHostCapabilities', 'Unrecognised host capabilities', {
      error: parsed.error,
    });

    return null;
  }

  return parsed.data;
}

/**
 * Subscribes to what the control box reports. A subscription rather than a
 * one-shot read because Apply has to reach every gated surface, not just the
 * panel that pressed it.
 */
export function onLoadHostCapabilities(
  subscribe: (capabilities: HostCapabilitiesData) => void,
): () => void {
  const sub = getService('hostCapabilities')()
    // `smart`, the default, folds a patched event in with `.filter()` and
    // `.sort()`. This service answers with one object, so that throws and
    // silently kills the stream. `always` re-reads instead of merging.
    .watch({ listStrategy: 'always' })
    .find({ query: {} })
    .pipe(map(one), filter(isNotNull))
    .subscribe({
      next: subscribe,
      error: (error) => {
        // Null keeps the gate closed, but an errored stream never recovers.
        log.error('onLoadHostCapabilities', 'Subscription ended', { error });
      },
    });

  return () => sub.unsubscribe();
}

/**
 * Every `useHostCapabilities` subscriber sees the change, so the patch's own
 * response is not read back.
 */
export async function setSimulatedProcessor(
  simulatedPresetId: HostCapabilitiesData['simulatedPresetId'],
): Promise<void> {
  await getService('hostCapabilities')().patch(HOST_CAPABILITIES_ID, {
    simulatedPresetId,
  });
}

/**
 * One upstream subscription shared by every `useHostCapabilities` caller, with
 * the last answer replayed to whoever mounts next.
 *
 * Per-hook subscriptions would open one watch per mounted consumer — the step
 * library alone mounts a modal per step row — and would let two gated surfaces
 * resolve the host at different moments, so Play and the step rows could
 * disagree while both were settling.
 */
const subscribers = new Set<(capabilities: HostCapabilitiesData) => void>();

let latest: HostCapabilitiesData | null = null;
let unsubscribeUpstream: (() => void) | undefined;

function subscribeShared(
  subscribe: (capabilities: HostCapabilitiesData) => void,
): () => void {
  subscribers.add(subscribe);

  if (latest !== null) subscribe(latest);

  unsubscribeUpstream ??= onLoadHostCapabilities((capabilities) => {
    latest = capabilities;
    subscribers.forEach((notify) => notify(capabilities));
  });

  return () => {
    subscribers.delete(subscribe);

    // Keep `latest` for the next mount: it is what the host last said, and
    // dropping it would send a remount back through the null "not answered
    // yet" state that closes every gate.
    if (subscribers.size === 0) {
      unsubscribeUpstream?.();
      unsubscribeUpstream = undefined;
    }
  };
}

/** Test seam: drops the shared subscription and its replayed answer. */
export function resetHostCapabilitiesForTests(): void {
  subscribers.clear();
  unsubscribeUpstream?.();
  unsubscribeUpstream = undefined;
  latest = null;
}

/** Host capabilities, or null until the host has answered. */
export function useHostCapabilities(): HostCapabilitiesData | null {
  const [capabilities, setCapabilities] = useState<HostCapabilitiesData | null>(
    () => latest,
  );

  useEffect(() => subscribeShared(setCapabilities), []);

  return capabilities;
}
